import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Info, Loader2, PlugZap, Save, Workflow } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { cn, formatarDataHora, paraBooleano } from '@/lib/utils';
import type { ConexaoN8n } from '@/features/notificacoes/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';

const esquema = z.object({
  urlWebhook: z.string().min(1, 'Informe a URL do webhook.'),
  segredo: z.string().optional(),
  timeoutMs: z.coerce.number().int().min(2000).max(60000),
  ativo: z.boolean(),
});

type Formulario = z.infer<typeof esquema>;

interface ResultadoTeste {
  ok: boolean;
  mensagem: string;
}

export function PaginaN8n() {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = React.useState<ResultadoTeste | null>(null);

  const { data: conexao, isPending } = useQuery({
    queryKey: ['n8n-conexao'],
    queryFn: () => api.get<ConexaoN8n>('/n8n/conexao'),
  });

  const formulario = useForm<z.input<typeof esquema>, unknown, Formulario>({
    resolver: zodResolver(esquema),
    values: {
      urlWebhook: conexao?.urlWebhook ?? '',
      segredo: '',
      timeoutMs: conexao?.timeoutMs ?? 10_000,
      ativo: conexao?.ativo ?? false,
    },
  });

  const testar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<ResultadoTeste>('/n8n/conexao/testar', {
        urlWebhook: dados.urlWebhook,
        segredo: dados.segredo || undefined,
        timeoutMs: dados.timeoutMs,
      }),
    onSuccess: async (r) => {
      setResultado(r);
      await queryClient.invalidateQueries({ queryKey: ['n8n-conexao'] });
    },
    onError: (erro) =>
      setResultado({
        ok: false,
        mensagem: erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao testar.',
      }),
  });

  const salvar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.patch<ConexaoN8n>('/n8n/conexao', { ...dados, segredo: dados.segredo || undefined }),
    onSuccess: async () => {
      toast.success('Configuração salva.');
      formulario.setValue('segredo', '');
      await queryClient.invalidateQueries({ queryKey: ['n8n-conexao'] });
      await queryClient.invalidateQueries({ queryKey: ['n8n-situacao'] });
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const aoSalvar = formulario.handleSubmit((d) => salvar.mutate(d));
  const aoTestar = formulario.handleSubmit((d) => testar.mutate(d));

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const ativo = formulario.watch('ativo');

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Envio por WhatsApp (n8n)"
        descricao="O KAVI não fala com o WhatsApp diretamente: publica o agendamento em um webhook do n8n, que decide como entregar."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Desativado, ninguém deixa de ser avisado.</p>
          <p className="mt-0.5">
            O e-mail ao paciente e a cópia à UBS continuam saindo normalmente. O WhatsApp é um canal
            a mais, não o único.
          </p>
        </div>
      </div>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Workflow className="size-4" aria-hidden="true" />
            Webhook
          </CartaoTitulo>
          <CartaoDescricao>
            {conexao?.ultimoTesteEm ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Último teste em {formatarDataHora(conexao.ultimoTesteEm)}
                <Etiqueta tom={conexao.ultimoTesteOk ? 'sucesso' : 'perigo'}>
                  {conexao.ultimoTesteOk ? 'respondeu' : 'com problema'}
                </Etiqueta>
              </span>
            ) : (
              'O webhook ainda não foi testado.'
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <form onSubmit={(e) => void aoSalvar(e)} noValidate className="flex flex-col gap-4">
            <Campo
              id="n8n-url"
              rotulo="URL do webhook"
              obrigatorio
              ajuda="Endereço do nó Webhook no fluxo do n8n."
              erro={formulario.formState.errors.urlWebhook?.message}
            >
              <Entrada
                placeholder="https://n8n.exemplo.gov.br/webhook/kavi"
                {...formulario.register('urlWebhook')}
              />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="n8n-segredo"
                rotulo="Segredo compartilhado"
                obrigatorio={!conexao?.temSegredo}
                ajuda={
                  conexao?.temSegredo
                    ? 'Já existe um segredo salvo. Deixe em branco para mantê-lo.'
                    : 'O KAVI assina cada chamada com ele; o fluxo confere a assinatura.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  placeholder={conexao?.temSegredo ? '••••••••' : ''}
                  {...formulario.register('segredo')}
                />
              </Campo>

              <Campo
                id="n8n-timeout"
                rotulo="Tempo limite (ms)"
                ajuda="Entre 2.000 e 60.000."
                erro={formulario.formState.errors.timeoutMs?.message}
              >
                <Entrada type="number" inputMode="numeric" {...formulario.register('timeoutMs')} />
              </Campo>
            </div>

            <Campo
              id="n8n-ativo"
              rotulo="Situação da integração"
              ajuda="Desativado, o agendamento não cria a notificação de WhatsApp."
            >
              <Selecao {...formulario.register('ativo', { setValueAs: paraBooleano })}>
                <option value="false">Desativado</option>
                <option value="true">Ativo</option>
              </Selecao>
            </Campo>

            {ativo && !conexao?.ultimoTesteOk && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Teste o webhook antes de ativar: sem isso, cada agendamento gera uma notificação de
                WhatsApp que vai falhar em silêncio.
              </p>
            )}

            <div className="border-borda text-silenciado-texto rounded-md border border-dashed p-3 text-xs">
              <p className="text-fundo-texto font-medium">O que o KAVI envia</p>
              <p className="mt-1">
                Protocolo, nome e telefone do paciente, dados do agendamento e uma mensagem já
                pronta em <code>mensagemSugerida</code>. Cada chamada leva os cabeçalhos{' '}
                <code>X-KAVI-Correlacao</code> e <code>X-KAVI-Assinatura</code> (HMAC-SHA256 do
                corpo). Nenhum dado clínico é enviado.
              </p>
            </div>

            {resultado && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-md border p-3 text-sm',
                  resultado.ok
                    ? 'border-status-agendado-borda bg-status-agendado-fundo text-status-agendado-texto'
                    : 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto',
                )}
              >
                {resultado.ok ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">{resultado.mensagem}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => void aoTestar()}
                disabled={testar.isPending}
              >
                {testar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <PlugZap aria-hidden="true" />
                )}
                {testar.isPending ? 'Testando…' : 'Testar webhook'}
              </Botao>

              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>
            </div>
          </form>
        </CartaoConteudo>
      </Cartao>
    </div>
  );
}
